import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize, useViewer } from '@treams/treams-hooks';
import { AddIcon, ChevronRightIcon, SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Career, ExpertisePartialFragment, User } from '@treams/treams-network';
import {
	Breakpoint,
	Button,
	GraphQLErrorMessage,
	Label,
	Popover,
	Sidebar,
	Skeleton,
} from '@treams/treams-ui';

import CreateJobPosition from '../../settings/employees/job-positions/CreateJobPosition';
import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import { GrowthPathSidebarMyJobPositionItem } from './GrowthPathSidebarMyJobPositionItem';

const ScrollContainer = styled(Box)`
	@media screen and (min-width: ${Breakpoint.Medium}) {
		height: 100%;
	}
`;

const GroupTitle = styled(Label)`
	display: block;
	padding-left: 0;
`;

const GrowthPathSidebar = () => {
	const viewer = useViewer();
	const { localize } = useLocalize();
	const location = useLocation();
	const { t } = useTranslation();
	const multiRoleConversion = useFeature(graphql.FeatureName.MultiRoleConversion);
	const hasSearchOnSkills = useFeature(graphql.FeatureName.SearchOnSkills);
	const hasJobCompetenciesExplorer = useFeature(graphql.FeatureName.JobCompetenciesExplorer);

	const { userID, careerID, jobTitleID } = useGrowthPathContext();

	const queryParams = new URLSearchParams(location.search);
	const stringifiedQueryParams = queryParams.toString();
	const queryParamsString = stringifiedQueryParams ? `?${stringifiedQueryParams}` : '';

	const {
		data: userData,
		loading: userLoading,
		error: userError,
		refetch: userRefetch,
	} = graphql.useUserQuery({
		variables: { id: userID ?? viewer!.id },
	});
	const {
		data: userCompetenciesData,
		loading: userCompetenciesLoading,
		error: userCompetenciesError,
	} = graphql.useUserCompetenciesQuery({
		variables: { id: userID ?? viewer!.id },
	});
	const {
		data: expertisesData,
		loading: expertisesLoading,
		error: expertisesError,
	} = graphql.usePaginatedExpertisesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const {
		data: careersData,
		loading: careersLoading,
		error: careersError,
	} = graphql.useCareersQuery({
		variables: { isArchived: false, first: 1000 },
	});
	const {
		data: jobTitlesData,
		loading: jobTitlesLoading,
		error: jobTitlesError,
	} = graphql.useJobTitlesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const [open, setOpen] = useState(false);
	const [addingRole, setAddingRole] = useState(false);

	const loading =
		userCompetenciesLoading ||
		expertisesLoading ||
		careersLoading ||
		jobTitlesLoading ||
		userLoading;
	const error =
		userCompetenciesError || expertisesError || careersError || jobTitlesError || userError;

	useEffect(() => {
		if (location.pathname.includes('/growth-path/career/')) {
			setOpen(true);
		}
	}, [location.pathname]);

	const filteredExpertises = expertisesData?.paginatedExpertises.edges.filter(
		(edge) => edge.node.careers.length > 0,
	);

	const allJobTitles = jobTitlesData?.jobTitles.edges
		.map((edge) => {
			return edge.node;
		})
		.sort((a, b) => {
			return localize(a.title).text.localeCompare(localize(b.title).text);
		});

	const careerJobTitles = careersData?.careers.edges.flatMap((edge) => {
		return edge.node.jobTitles;
	});

	const filteredJobTitles = allJobTitles?.filter(
		(jobTitle) => !careerJobTitles?.some((careerJobTitle) => careerJobTitle.id === jobTitle.id),
	);

	const dropdownIsOpen = (expertise: ExpertisePartialFragment) => {
		return expertise.careers.map((career: Partial<Career>) => career.id).includes(careerID);
	};

	const mappedJobPositions = userCompetenciesData?.user.jobPositions.map((jobPosition) => {
		return {
			id: jobPosition.id,
			jobTitle: jobPosition.jobTitle,
			startDate: jobPosition.startDate,
			endDate: jobPosition.endDate,
			externalReferenceID: jobPosition.externalReferenceID,
			jobLevelID: jobPosition.jobLevel?.id,
		};
	});

	if (loading) {
		return <GrowthPathSidebarSkeleton />;
	}
	if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	}

	const create = (
		<CreateJobPosition
			translationKey="roles"
			hideJobLevel
			user={userData!.user as User}
			onCancel={() => setAddingRole(false)}
			onCreate={async () => {
				await userRefetch();
			}}
			onDismiss={() => setAddingRole(false)}
		/>
	);

	return (
		<ScrollContainer>
			<Sidebar>
				{hasSearchOnSkills && (
					<Button
						type="secondary"
						icon={<SearchIcon />}
						title={t('skills.explorer.search')}
						rightIcon={<ChevronRightIcon />}
						link="/growth-path/search"
						mb={2}
					/>
				)}

				<Sidebar.Section
					title={<GroupTitle mb={1}>{t('growthPath.explorer.jobTitles.my')}</GroupTitle>}
				>
					<Sidebar.Item
						small
						title={t('growthPath.explorer.jobTitles.all')}
						path={
							userID
								? `/users/${userID}/growth-path/my-job-title/all${queryParamsString}`
								: `/growth-path/my-job-title/all${queryParamsString}`
						}
						selected={location.pathname.includes('/growth-path/my-job-title/all')}
					/>

					{mappedJobPositions?.map((jobPosition, index) => (
						<Fragment key={index}>
							{multiRoleConversion && userID ? (
								<GrowthPathSidebarMyJobPositionItem key={index} jobPosition={jobPosition} />
							) : (
								<Sidebar.Item
									small
									key={index}
									title={localize(jobPosition.jobTitle.title).text}
									path={
										userID
											? `/users/${userID}/growth-path/my-job-title/${jobPosition.jobTitle.id}${queryParamsString}`
											: `/growth-path/my-job-title/${jobPosition.jobTitle.id}${queryParamsString}`
									}
									selected={location.pathname.includes(
										`/growth-path/my-job-title/${jobPosition.jobTitle.id}`,
									)}
								></Sidebar.Item>
							)}
						</Fragment>
					))}

					{userID && multiRoleConversion && (
						<Box my={2}>
							<Popover.Target visible={addingRole} contents={create}>
								<Button
									onClick={() => setAddingRole(true)}
									title={t('sidebar.user.addRole')}
									icon={<AddIcon />}
									type="secondary"
								/>
							</Popover.Target>
						</Box>
					)}
				</Sidebar.Section>

				{hasJobCompetenciesExplorer && (
					<>
						<Sidebar.Section
							title={
								<GroupTitle mb={1}>{t('growthPath.explorer.jobTitles.expertises')}</GroupTitle>
							}
						>
							{filteredExpertises?.map(({ node: expertise }, index) => (
								<Sidebar.ItemDropdown
									key={index}
									title={localize(expertise.title).text}
									isOpen={
										dropdownIsOpen(expertise) ||
										!!(open && expertise.careers.some((career) => career.id === careerID))
									}
								>
									{expertise.careers.map((career, index) => (
										<Sidebar.Item
											small
											key={index}
											title={localize(career.title).text}
											path={
												userID
													? `/users/${userID}/growth-path/career/${career.id}/job-title/${jobTitleID}${queryParamsString}`
													: `/growth-path/career/${career.id}/job-title/${jobTitleID}${queryParamsString}`
											}
											selected={location.pathname.includes(
												`/growth-path/career/${career.id}/job-title/${jobTitleID}`,
											)}
										/>
									))}
								</Sidebar.ItemDropdown>
							))}
						</Sidebar.Section>

						{filteredExpertises && filteredExpertises.length === 0 && (
							<Sidebar.ItemDropdown
								title={t('growthPath.sidebar.other.careers')}
								isOpen={location.pathname.includes('/growth-path/job-title')}
							>
								{filteredJobTitles?.map((jobTitle, index) => (
									<Sidebar.Item
										small
										key={index}
										title={localize(jobTitle.title).text}
										path={
											userID
												? `/users/${userID}/growth-path/job-title/${jobTitle.id}${queryParamsString}`
												: `/growth-path/job-title/${jobTitle.id}${queryParamsString}`
										}
										selected={location.pathname.includes(`/growth-path/job-title/${jobTitle.id}`)}
									/>
								))}
							</Sidebar.ItemDropdown>
						)}
					</>
				)}
			</Sidebar>
		</ScrollContainer>
	);
};

const GrowthPathSidebarSkeleton = () => {
	return (
		<Sidebar>
			<Box my={3}>
				<Skeleton.Box height={24} flexGrow={1} />
			</Box>
			<Skeleton.Repeat count={2}>
				<Sidebar.ItemDropdown.Skeleton />
			</Skeleton.Repeat>
		</Sidebar>
	);
};

export default GrowthPathSidebar;
